import { api } from '../api/client';

/** The VAPID public key arrives base64url-encoded; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(safe);
  return new Uint8Array(raw.split('').map((c) => c.charCodeAt(0)));
}

export function isWebPushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Already-decided permission, without prompting — for painting the right UI state on load. */
export function webPushPermission(): NotificationPermission | 'unsupported' {
  return isWebPushSupported() ? Notification.permission : 'unsupported';
}

/**
 * Request permission (if not already answered), register the service
 * worker, subscribe, and hand the subscription to the server. Idempotent —
 * calling it again with an existing subscription just re-confirms it server-side.
 */
export async function subscribeWebPush(): Promise<{ ok: boolean; reason?: string }> {
  if (!isWebPushSupported()) return { ok: false, reason: 'This browser does not support push notifications.' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'Notification permission was not granted.' };

  const { publicKey } = await api.get<{ publicKey: string | null }>('/api/push/vapid-public-key');
  if (!publicKey) return { ok: false, reason: 'The server has no push keys configured yet.' };

  const registration = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return { ok: false, reason: 'The browser returned an incomplete subscription.' };
  }
  await api.post('/api/push/web-subscribe', { endpoint: json.endpoint, keys: json.keys });
  return { ok: true };
}

export async function unsubscribeWebPush(): Promise<void> {
  if (!isWebPushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await api.delete(`/api/push/web-subscribe?endpoint=${encodeURIComponent(endpoint)}`);
}
